import Groq from "groq-sdk";

class GroqService {
  constructor() {
    this.apiKey = process.env.REACT_APP_GROQ_API_KEY;
    this.client = null;

    if (this.apiKey) {
      this.client = new Groq({
        apiKey: this.apiKey,
        dangerouslyAllowBrowser: true // Required for client-side usage
      });
    }
  }

  // System context about KwachaPlus
  getSystemContext() {
    return `You are an AI assistant for KwachaPlus, a lending management system. You help users with:

SYSTEM OVERVIEW:
- KwachaPlus is a React-based lending management platform
- Backend: Directus CMS at https://zedlenders.pickmesms.com/
- Real-time features via WebSocket
- Role-based access control (Administrator, Branch Manager, Loan Officer, etc.)

KEY FEATURES:
1. Loan Management: Create, approve, disburse, and track loans
2. Borrower Management: Manage borrower profiles, credit scores, KYC
3. Payments & Transactions: Record payments, track repayments
4. Collateral Management: Track vehicle collateral, GPS tracking
5. Credit Scoring: Automated credit assessment
6. Reporting: Loan statements, account statements, PDF generation
7. Collections: Track overdue loans, payment reminders

LOAN CALCULATIONS:
- Interest Rate: 4% per month
- Processing Fee: 6%
- Service Fee: 5%
- Underwriting Fee: 5%
- Admin Fee: 8%
- Total Fees: 24% (excluding interest)
- GPS Tracker: ZMW 400 per month

COMMON TASKS:
- Creating new loans: Navigate to Loans → Add New Loan
- Approving loans: Loan Details → Approve Loan button
- Recording payments: Transactions → Add Payment
- Generating statements: Loan Details → Print Statement
- Viewing borrower info: Borrowers → Select borrower

TECHNICAL DETAILS:
- Commands: npm start (dev), npm run build (production), npm test
- PDF generation uses jsPDF and @react-pdf/renderer
- State management via React Context (AppContext)
- Authentication: JWT-based with localStorage

USER ROLES & PERMISSIONS:
- Administrator: Full system access
- Loan Officer: Create/manage loans, borrowers
- Accountant: Financial operations, reports
- Collections Manager: Track/manage overdue loans
- Borrower: View own loan status, make payments

Be helpful, concise, and provide step-by-step guidance when users ask how to perform tasks.`;
  }

  async chat(messages, options = {}) {
    if (!this.client) {
      throw new Error("Groq API key not configured. Please add REACT_APP_GROQ_API_KEY to your .env file");
    }

    try {
      const systemMessage = {
        role: "system",
        content: this.getSystemContext()
      };

      const chatCompletion = await this.client.chat.completions.create({
        messages: [systemMessage, ...messages],
        model: options.model || "llama-3.3-70b-versatile",
        temperature: options.temperature || 0.7,
        max_tokens: options.max_tokens || 1024,
        top_p: options.top_p || 1,
        stream: options.stream || false,
      });

      return chatCompletion.choices[0]?.message?.content || "I'm sorry, I couldn't generate a response.";
    } catch (error) {
      console.error("Groq API Error:", error);

      if (error.status === 401) {
        throw new Error("Invalid Groq API key. Please check your configuration.");
      } else if (error.status === 429) {
        throw new Error("Rate limit exceeded. Please try again in a moment.");
      } else {
        throw new Error(`Failed to get AI response: ${error.message}`);
      }
    }
  }

  async streamChat(messages, onChunk, options = {}) {
    if (!this.client) {
      throw new Error("Groq API key not configured");
    }

    try {
      const systemMessage = {
        role: "system",
        content: this.getSystemContext()
      };

      const stream = await this.client.chat.completions.create({
        messages: [systemMessage, ...messages],
        model: options.model || "llama-3.3-70b-versatile",
        temperature: options.temperature || 0.7,
        max_tokens: options.max_tokens || 1024,
        stream: true,
      });

      let fullResponse = "";

      for await (const chunk of stream) {
        const content = chunk.choices[0]?.delta?.content || "";
        fullResponse += content;
        if (onChunk && content) {
          onChunk(content, fullResponse);
        }
      }

      return fullResponse;
    } catch (error) {
      console.error("Groq Stream Error:", error);
      throw error;
    }
  }
}

export default new GroqService();
